import { useParams, Navigate, Link } from "react-router-dom";
import { Share2, Link as LinkIcon, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { JOURNAL } from "@/lib/journal";
import { JournalCard } from "@/pages/Journal";
import { useSeo } from "@/hooks/useSeo";
import { Reveal } from "@/components/Reveal";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// One reusable template for articles, guides, stories, trip recaps, and podcasts.
export default function JournalArticle() {
  const { slug } = useParams();
  const item = JOURNAL.find((j) => j.slug === slug);
  useSeo({ title: item ? `${item.title} | The Passport Journal` : "The Passport Journal", description: item?.excerpt, noindex: true, image: item?.cover });
  if (!item) return <Navigate to="/journal" replace />;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: item.title, text: item.excerpt, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };
  const copy = async () => { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };

  const related = JOURNAL.filter((j) => j.slug !== slug && j.category === item.category).slice(0, 3);

  return (
    <div className="bg-noir min-h-screen">
      <Navbar />
      <article className="pt-32 md:pt-40 pb-24 px-6 md:px-12">
        <div className="max-w-[820px] mx-auto">
          <Link to="/journal" className="font-body text-[11px] uppercase tracking-[0.2em] text-gold link-underline">← The Passport Journal</Link>
          <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mt-8 mb-4">{item.category}{item.readTime ? ` · ${item.readTime}` : ""}{item.date ? ` · ${item.date}` : ""}</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-[0.98] tracking-tight">{item.title}</h1>
          <div className="flex items-center gap-4 mt-8">
            <button onClick={share} data-testid="share-btn" className="inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 text-white/70 hover:border-gold hover:text-gold transition-colors font-body text-[11px] uppercase tracking-[0.15em]"><Share2 size={14} /> Share</button>
            <button onClick={copy} data-testid="copy-link-btn" className="inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 text-white/70 hover:border-gold hover:text-gold transition-colors font-body text-[11px] uppercase tracking-[0.15em]"><LinkIcon size={14} /> Copy Link</button>
          </div>
        </div>
        <div className="max-w-[1100px] mx-auto my-12 aspect-[16/9] overflow-hidden border border-white/10">
          <img src={item.cover} alt={item.title} className="w-full h-full object-cover" />
        </div>
        <div className="max-w-[720px] mx-auto space-y-6">
          {(item.type === "podcast" && item.audio) && <audio controls src={item.audio} className="w-full" data-testid="journal-audio" />}
          {item.video && <video controls src={item.video} className="w-full border border-white/10" data-testid="journal-video" />}
          {(item.content || []).map((b, i) => {
            if (b.t === "h2") return <h2 key={i} className="font-serif text-3xl font-light pt-6">{b.text}</h2>;
            if (b.t === "quote") return <blockquote key={i} className="border-l-2 border-gold pl-6 font-serif text-2xl italic text-white/80">{b.text}</blockquote>;
            if (b.t === "img") return <img key={i} src={b.src} alt={b.alt || ""} className="w-full border border-white/10" loading="lazy" />;
            return <p key={i} className="font-body text-white/75 text-lg leading-[1.85] font-light">{b.text}</p>;
          })}
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-20 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/10">
          <div className="max-w-[1500px] mx-auto">
            <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-10">More from The Journal</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{related.map((r, i) => <Reveal key={r.slug} delay={i * 0.08}><JournalCard item={r} /></Reveal>)}</div>
          </div>
        </section>
      )}

      <section className="py-20 px-6 md:px-12 bg-noir border-t border-white/10 text-center">
        <p className="font-serif text-2xl md:text-4xl font-light">Where will your passport take you next?</p>
        <Link to="/start-your-journey" className="mt-8 inline-flex items-center gap-3 border border-gold text-gold px-10 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-gold hover:text-black transition-colors duration-300">Start Your Journey <ArrowRight size={15} /></Link>
      </section>
      <Footer />
    </div>
  );
}
